import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
      <div className="w-full max-w-sm">
        <div className="mb-10 text-center">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_83ef92c5-3c5d-4a35-b89a-364f4b1c35a2/artifacts/htwqujap_1000X1000.png"
            alt="Ami Video"
            className="h-20 mx-auto mb-5 dark:brightness-0 dark:invert"
          />
          <h1 className="text-3xl font-bold tracking-tight mb-2">Ami Video</h1>
          <p className="text-xs text-muted-foreground tracking-[0.25em] uppercase">
            Photography is Poetry
          </p>
        </div>

        <div className="mb-8 text-center">
          <h2 className="text-2xl font-semibold tracking-tight mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-2xl text-sm" data-testid="login-error-message">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-muted-foreground mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent text-foreground focus:outline-none focus:bg-card focus:border-black/20 focus:ring-2 focus:ring-black/10 transition-all"
              placeholder="Enter your email"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-muted-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 pr-11 rounded-2xl bg-secondary border border-transparent text-foreground focus:outline-none focus:bg-card focus:border-black/20 focus:ring-2 focus:ring-black/10 transition-all"
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                data-testid="login-toggle-password-visibility"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="mt-3 text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-medium hover:bg-black/90 transition-all active:scale-[0.98] disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
